import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Firebase is OPTIONAL — it powers phone/OTP auth and browser web-push, neither
// of which the app requires (login is email + password). When VITE_FIREBASE_*
// isn't configured, `initializeApp`/`getAuth` would throw `auth/invalid-api-key`
// at import time and break the whole bundle, so only initialise when a real API
// key is present. `auth` is then null; callers that use Firebase auth/messaging
// must tolerate that.
export const firebaseEnabled = Boolean(firebaseConfig.apiKey);

const app = firebaseEnabled ? initializeApp(firebaseConfig) : null;
export const auth = app ? getAuth(app) : null;

// Web Push (VAPID) key from the Firebase console → Cloud Messaging → Web Push
// certificates. Without it getToken() can't mint a web registration token.
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

// Returns a memoized messaging instance, or null in browsers that can't do web
// push (Safari <16, private windows, unsupported environments). Callers must
// tolerate null so the rest of the app is unaffected.
let messagingPromise;
export function getMessagingIfSupported() {
  if (!messagingPromise) {
    messagingPromise = app
      ? isSupported()
          .then((supported) => (supported ? getMessaging(app) : null))
          .catch(() => null)
      : Promise.resolve(null);
  }
  return messagingPromise;
}

// Requests notification permission, registers the FCM service worker, and
// returns a web-push registration token (or null if unavailable / denied).
export async function requestWebPushToken() {
  if (!VAPID_KEY) return null;
  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;

  if (Notification.permission === "denied") return null;
  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted") return null;

  const registration = await navigator.serviceWorker.register(
    "/firebase-messaging-sw.js"
  );

  return getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
}

// Subscribes to foreground pushes. Returns an unsubscribe fn (or a no-op).
export async function onForegroundPush(handler) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
